import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useMutation, useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { toast } from "sonner";
import { usePageMetadata } from "@/hooks/use-page-metadata";
import { Plus, Edit, Trash2, Eye, EyeOff } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router";

export default function AdminBlogs() {
  const { HelmetTags } = usePageMetadata("/admin/cms/blogs");
  const navigate = useNavigate();

  const blogPosts = useQuery((api as any).blogPosts.getAllBlogPosts);
  const deletePost = useMutation((api as any).blogPosts.deleteBlogPost);
  const publishPost = useMutation((api as any).blogPosts.publishBlogPost);
  const unpublishPost = useMutation((api as any).blogPosts.unpublishBlogPost);

  const handleDelete = async (id: string) => {
    if (confirm("Are you sure you want to delete this blog post?")) {
      try {
        await deletePost({ id: id as any });
        toast.success("Blog post deleted successfully");
      } catch (error) {
        toast.error("Failed to delete blog post");
      }
    }
  };

  const handleTogglePublish = async (post: any) => {
    try {
      if (post.isPublished) {
        await unpublishPost({ id: post._id });
        toast.success("Blog post unpublished");
      } else {
        await publishPost({ id: post._id });
        toast.success("Blog post published");
      }
    } catch (error) {
      toast.error("Failed to update publish status");
    }
  };

  return (
    <>
      <HelmetTags />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="min-h-screen"
      >
        <Navbar />
        <main className="pt-32 pb-16 min-h-screen">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h1 className="text-4xl font-bold tracking-tight mb-2">Blog Management</h1>
                <p className="text-muted-foreground">Create and manage blog posts</p>
              </div>
              <Button size="lg" onClick={() => navigate("/admin/cms/blogs/create")}>
                <Plus className="h-5 w-5 mr-2" />
                New Blog Post
              </Button>
            </div>

            <div className="grid gap-6">
              {blogPosts?.map((post: any) => (
                <Card key={post._id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <CardTitle>{post.title}</CardTitle>
                          <Badge variant={post.isPublished ? "default" : "secondary"}>
                            {post.isPublished ? "Published" : "Draft"}
                          </Badge>
                          <Badge variant="outline">{post.category}</Badge>
                        </div>
                        <CardDescription>{post.excerpt}</CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleTogglePublish(post)}
                        >
                          {post.isPublished ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => navigate(`/admin/cms/blogs/${post._id}`)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleDelete(post._id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
              {(!blogPosts || blogPosts.length === 0) && (
                <div className="text-center py-12 text-muted-foreground">
                  No blog posts found. Create one to get started.
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </motion.div>
    </>
  );
}